import { Component } from '@angular/core';
import { filter, map, switchMap } from 'rxjs/operators';

import { AppleDevice, Device } from '@era/locations-sdk';
import { Store } from '@ngrx/store';

import { combineLatest, Observable } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import {
  AppleProfilesStoreActions,
  CategoryApplicationsStoreSelectors,
  CategoryStoreSelectors,
  DeviceStoreActions,
  DeviceStoreSelectors,
  RouterStoreStates,
} from '@era/stores';
import { AppleDeviceComponent } from '../../../shared/components/apple-device/apple-device.component';
import { AppleDeviceService } from '../../../shared/components/apple-device/apple-device.service';
import { AppleApplicationLockChanged } from '../../models/apple-application-lock-changed.model';
import { AppleDeviceDesiredNameChanged } from '../../models/apple-device-desired-name-changed.model';
import {
  AllowedOsUpdatesStoreActions,
  AllowedOsUpdatesStoreSelectors,
  AllowedOsUpdatesStoreStates,
} from '../../sub-features/device-os-updates/device-allowed-os-updates-store';
import { DeviceActionBarContainerComponent } from '../device-action-bar/device-action-bar.container';
import { DeviceOsUpdateStatusContainerComponent } from '../device-os-update-status/device-os-update-status.container';
import { DeviceScheduledTasksContainerComponent } from '../device-scheduled-tasks/device-scheduled-tasks.container';
import { DeviceSwappedCurrentComponent } from '../device-swapped-current/device-swapped-current.component';
import { DeviceSyncStatusContainerComponent } from '../device-sync-status/device-sync-status.container';

@Component({
  selector: 'era-apple-device-current',
  templateUrl: './apple-device-current.container.html',
  styleUrls: ['./apple-device-current.container.scss'],
  imports: [
    AppleDeviceComponent,
    DeviceActionBarContainerComponent,
    DeviceOsUpdateStatusContainerComponent,
    DeviceSyncStatusContainerComponent,
    DeviceSwappedCurrentComponent,
    DeviceScheduledTasksContainerComponent,
    RouterOutlet,
    AsyncPipe,
  ],
})
export class AppleDeviceCurrentContainerComponent {
  public readonly appleDevice$: Observable<AppleDevice>;
  public readonly device$: Observable<Device>;
  public readonly isAtLatestAllowedOsVersion$: Observable<boolean>;
  public readonly isMdmLostModeEnabled$: Observable<boolean>;

  constructor(
    private readonly store: Store<RouterStoreStates.RouterState>,
    private readonly appleDeviceStore: Store<AppleDevice>,
    private readonly deviceStore: Store<Device>,
    private readonly updatesStore: Store<AllowedOsUpdatesStoreStates.AllowedOsUpdateState>,
    private readonly appleDeviceService: AppleDeviceService
  ) {
    this.device$ = this.appleDeviceStore
      .select(DeviceStoreSelectors.selectDeviceByCurrentAppleDevice)
      .pipe(filter((device) => device !== undefined));

    const category$ = this.device$.pipe(
      switchMap((device) => this.store.select(CategoryStoreSelectors.selectCategoryByDevice(device)))
    );

    this.appleDevice$ = combineLatest([category$, this.device$]).pipe(
      switchMap(([category, device]) =>
        this.appleDeviceStore
          .select(CategoryApplicationsStoreSelectors.selectApplicationsForDeviceWithConfiguration(category, device))
          .pipe(
            filter((device) => device !== undefined),
            switchMap((device) => this.appleDeviceService.observeAppleDeviceWithLockedFlag(device))
          )
      )
    );

    this.isMdmLostModeEnabled$ = this.appleDevice$.pipe(map((device) => device.isMdmLostModeEnabled));

    this.isAtLatestAllowedOsVersion$ = this.updatesStore.select(
      AllowedOsUpdatesStoreSelectors.selectIsCurrentDeviceAtLatestAllowedOsVersion
    );
  }

  public updateDevice(event: string) {
    this.appleDeviceStore.dispatch(
      AllowedOsUpdatesStoreActions.CurrentDeviceViewActions.updateToLatestAllowedVersion({
        deviceId: event,
      })
    );
  }

  public updateDesiredName(event: AppleDeviceDesiredNameChanged) {
    this.deviceStore.dispatch(
      DeviceStoreActions.DeviceActionsActions.setDesiredName({
        deviceId: event.deviceId,
        newName: event.newDesiredName,
      })
    );
  }

  public applicationLockChanged(event: AppleApplicationLockChanged) {
    this.appleDeviceStore.dispatch(
      AppleProfilesStoreActions.AppleDeviceContainerActions.singleAppModeChanged({
        udid: event.udid,
        bundleIdentifier: event.bundleIdentifier,
        locked: event.locked,
      })
    );
  }
}
